<template>
  <!-- EcountEtl 데드레터 목록 (SPEC-ECOUNT-ETL-OPS-CONSOLE-001 REQ-OC-DEADLETTERS-001~006) -->
  <div class="page-container-v2">
    <div class="page-header">
      <span class="page-title">{{ $t('ecountEtl_deadLetters_title') }}</span>
      <div style="display: flex; align-items: center; gap: 8px; margin-left: auto;">
        <timezone-toggle :current-tz="currentTz" @toggle="toggleTz" />
        <refresh-toggle
          :enabled="autoRefreshEnabled"
          :last-fetch-at="lastFetchAt"
          :loading="loading"
          :seconds-until-next="secondsUntilNext"
          @toggle="onToggleAutoRefresh"
          @force-refresh="fetchDeadLetters"
        />
        <a-button size="small" @click="showShortcutHelp = true">?</a-button>
      </div>
    </div>

    <!-- 4 reason 탭 -->
    <a-tabs v-model:activeKey="activeReason" style="margin-bottom: 0;" @change="onReasonChange">
      <a-tab-pane key="" :tab="$t('ecountEtl_deadLetters_tab_all')" />
      <a-tab-pane key="schema_error" :tab="$t('ecountEtl_deadLetters_tab_schema')" />
      <a-tab-pane key="mapping_error" :tab="$t('ecountEtl_deadLetters_tab_mapping')" />
      <a-tab-pane key="db_error" :tab="$t('ecountEtl_deadLetters_tab_db')" />
      <a-tab-pane key="unknown" :tab="$t('ecountEtl_deadLetters_tab_unknown')" />
    </a-tabs>

    <!-- 필터 -->
    <a-card style="margin-bottom: 16px; border-top: none; border-radius: 0 0 8px 8px;">
      <a-form layout="inline" :model="filters" @finish="onSearch">
        <a-form-item>
          <a-input
            ref="searchInput"
            v-model:value="filters.keyword"
            :placeholder="$t('ecountEtl_products_search_placeholder')"
            allow-clear
            style="width: 240px;"
          />
        </a-form-item>
        <a-form-item :label="$t('ecountEtl_runs_filter_dateRange')">
          <a-range-picker v-model:value="filters.dateRange" style="width: 240px;" />
        </a-form-item>
        <a-form-item>
          <a-button type="primary" html-type="submit" :loading="loading">
            {{ $t('ecountEtl_common_search') }}
          </a-button>
          <a-button style="margin-left: 8px;" @click="onReset">
            {{ $t('ecountEtl_common_reset') }}
          </a-button>
        </a-form-item>
      </a-form>
    </a-card>

    <!-- 오류 -->
    <a-alert
      v-if="error"
      type="error"
      :message="error"
      style="margin-bottom: 16px;"
      show-icon
      closable
      @close="error = null"
    />

    <!-- 데스크탑: 데드레터 테이블 -->
    <a-table
      v-if="!isMobileView"
      :data-source="deadLetters"
      :columns="columns"
      :loading="loading"
      :pagination="pagination"
      row-key="id"
      size="small"
      :scroll="{ x: 900 }"
      @change="onTableChange"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'createdAt'">
          {{ formatTime(record.createdAt) }}
        </template>
        <template v-if="column.key === 'retryCount'">
          <a-tag :color="(record.retryCount ?? 0) >= 3 ? 'red' : 'default'">
            {{ record.retryCount ?? 0 }}
          </a-tag>
        </template>
        <template v-if="column.key === 'actions'">
          <a-button size="small" type="link" @click="openRawModal(record)">
            {{ $t('ecountEtl_deadLetters_viewRaw') }}
          </a-button>
          <diagnostic-package-copy :dead-letter="record" style="margin-left: 4px;" />
        </template>
      </template>
    </a-table>

    <!-- 모바일: 카드뷰 -->
    <div v-else class="mobile-card-list">
      <div v-if="deadLetters.length === 0 && !loading" class="empty-state">
        <a-empty :description="$t('ecountEtl_common_empty')" />
      </div>
      <div v-for="record in deadLetters" :key="record.id" class="mobile-card">
        <div class="card-header">
          <span class="card-id mono">#{{ record.id }}</span>
          <a-tag :color="(record.retryCount ?? 0) >= 3 ? 'red' : 'default'">
            {{ $t('ecountEtl_deadLetters_col_retryCount') }}: {{ record.retryCount ?? 0 }}
          </a-tag>
        </div>
        <div class="card-field">
          <span class="card-label">{{ $t('ecountEtl_deadLetters_col_reason') }}</span>
          <span class="card-value">{{ record.reason || '-' }}</span>
        </div>
        <div class="card-field">
          <span class="card-label">{{ $t('ecountEtl_deadLetters_col_createdAt') }}</span>
          <span class="card-value">{{ formatTime(record.createdAt) }}</span>
        </div>
        <div class="card-actions">
          <a-button size="small" type="link" @click="openRawModal(record)">
            {{ $t('ecountEtl_deadLetters_viewRaw') }}
          </a-button>
          <diagnostic-package-copy :dead-letter="record" style="margin-left: 4px;" />
        </div>
      </div>
      <div class="pagination-row">
        <a-pagination
          v-model:current="pagination.current"
          :total="pagination.total"
          :page-size="pagination.pageSize"
          show-size-changer
          @change="onMobilePageChange"
          @show-size-change="onMobilePageChange"
        />
      </div>
    </div>

    <!-- Raw JSON 모달 -->
    <a-modal
      :open="rawModalVisible"
      :title="$t('ecountEtl_deadLetters_rawModal_title')"
      :footer="null"
      :width="700"
      @cancel="rawModalVisible = false"
    >
      <pre style="max-height: 500px; overflow: auto; font-size: 12px;">{{ rawJson }}</pre>
    </a-modal>

    <shortcut-help v-model:visible="showShortcutHelp" />
  </div>
</template>

<script>
// @MX:SPEC: SPEC-ECOUNT-ETL-OPS-CONSOLE-001 REQ-OC-DEADLETTERS-001~006

import { useAutoRefresh } from './composables/useAutoRefresh.js';
import { useKstTime } from './composables/useKstTime.js';
import { getDeadLetters, extractApiError, parsePaginatedResponse } from '../../apis/ecountEtl.js';
import DiagnosticPackageCopy from './_components/DiagnosticPackageCopy.vue';
import RefreshToggle from './_components/RefreshToggle.vue';
import TimezoneToggle from './_components/TimezoneToggle.vue';
import ShortcutHelp from './_components/ShortcutHelp.vue';
import dealerEcountListMixin from '@/mixins/dealerEcountListMixin';

// @MX:NOTE: [AUTO] SPEC-CHINA-GEO-EDITOR-FIELD-UX-001 모바일 스윕 — 목록 라우트에
// 카드뷰가 없던 유일한 EcountEtl 화면. Runs/Products/Transactions/SalesLines 패턴 답습.

export default {
  name: 'EcountEtlDeadLetters',
  components: { DiagnosticPackageCopy, RefreshToggle, TimezoneToggle, ShortcutHelp },
  mixins: [dealerEcountListMixin],

  setup() {
    const { currentTz, toggleTz, format } = useKstTime();
    const {
      enabled: autoRefreshEnabled,
      toggle: toggleAutoRefresh,
      lastFetchAt,
      secondsUntilNext,
    } = useAutoRefresh({ intervalMs: 30000, storageKey: 'ecountEtl.dlAutoRefresh' });
    return { currentTz, toggleTz, format, autoRefreshEnabled, toggleAutoRefresh, lastFetchAt, secondsUntilNext };
  },

  data() {
    return {
      loading: false,
      error: null,
      showShortcutHelp: false,
      activeReason: '',
      deadLetters: [],
      pagination: { current: 1, pageSize: 20, total: 0, showSizeChanger: true },
      filters: { keyword: '', dateRange: null },
      rawModalVisible: false,
      rawJson: '',
    };
  },

  computed: {
    columns() {
      return [
        { key: 'id', dataIndex: 'id', title: this.$t('ecountEtl_deadLetters_col_id'), width: 80 },
        { key: 'reason', dataIndex: 'reason', title: this.$t('ecountEtl_deadLetters_col_reason') },
        { key: 'retryCount', dataIndex: 'retryCount', title: this.$t('ecountEtl_deadLetters_col_retryCount'), width: 110 },
        { key: 'createdAt', dataIndex: 'createdAt', title: this.$t('ecountEtl_deadLetters_col_createdAt') },
        { key: 'actions', title: this.$t('ecountEtl_deadLetters_col_actions'), width: 200 },
      ];
    },
  },

  mounted() {
    this.fetchDeadLetters();
  },

  methods: {
    onToggleAutoRefresh(val) {
      this.toggleAutoRefresh(val);
    },
    onReasonChange() {
      this.pagination.current = 1;
      this.fetchDeadLetters();
    },
    onSearch() {
      this.pagination.current = 1;
      this.fetchDeadLetters();
    },
    onReset() {
      this.filters = { keyword: '', dateRange: null };
      this.pagination.current = 1;
      this.fetchDeadLetters();
    },
    onTableChange(pag) {
      this.pagination.current = pag.current;
      this.pagination.pageSize = pag.pageSize;
      this.fetchDeadLetters();
    },
    onMobilePageChange(current, pageSize) {
      this.pagination.current = current;
      if (typeof pageSize === 'number' && Number.isFinite(pageSize) && pageSize > 0) {
        this.pagination.pageSize = pageSize;
      }
      this.fetchDeadLetters();
    },
    async fetchDeadLetters() {
      this.loading = true;
      this.error = null;
      try {
        const params = {
          page: this.pagination.current,
          limit: this.pagination.pageSize,
          reason: this.activeReason || undefined,
          keyword: this.filters.keyword || undefined,
        };
        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          params.startDate = this.filters.dateRange[0].toISOString();
          params.endDate = this.filters.dateRange[1].toISOString();
        }
        const res = await getDeadLetters(params);
        const parsed = parsePaginatedResponse(res);
        this.deadLetters = parsed.items;
        this.pagination.total = parsed.total;
      } catch (e) {
        const err = extractApiError(e);
        const localized = this.$t(err.messageKey, err.messageParams);
        this.error = localized.startsWith('ecountEtl_') ? err.message : localized;
      } finally {
        this.loading = false;
      }
    },
    openRawModal(record) {
      this.rawJson = JSON.stringify(record.rawData ?? record, null, 2);
      this.rawModalVisible = true;
    },
    formatTime(value) {
      return this.format(value);
    },
  },
};
</script>

<style scoped>
.mobile-card-list { display: flex; flex-direction: column; gap: 12px; }
.empty-state { padding: 32px 0; text-align: center; }
.mobile-card {
  background: #fff; border: 1px solid #f0f0f0; border-radius: 8px;
  padding: 12px; box-shadow: 0 1px 2px rgba(0,0,0,0.04);
}
.card-header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid #f5f5f5;
}
.card-id.mono { font-family: monospace; font-weight: 600; color: #333; font-size: 13px; }
.card-field {
  display: flex; justify-content: space-between; align-items: baseline;
  font-size: 12px; padding: 4px 0; gap: 8px;
}
.card-label { color: #888; flex-shrink: 0; margin-right: 8px; }
.card-value { color: #333; text-align: right; word-break: break-word; }
.card-actions {
  display: flex; align-items: center; margin-top: 8px; padding-top: 8px;
  border-top: 1px solid #f5f5f5;
}
.pagination-row { display: flex; justify-content: center; margin-top: 16px; }
</style>
